import React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { MemoryRouter } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ItineraryPreview from "../components/ItineraryPreview";

async function captureAsPDF(container, filename) {
  container.classList.add("ip-pdf-export");

  await new Promise((r) =>
    requestAnimationFrame(() => requestAnimationFrame(r))
  );

  await Promise.all(
    Array.from(container.querySelectorAll("img")).map((img) =>
      img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((res) => {
            const done = () => res();
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
            setTimeout(done, 5000);
          })
    )
  );

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const usableWidth = pdfWidth - margin * 2;
  const usableHeight = pdfHeight - margin * 2;

  const ipContainer =
    container.querySelector(".ip-container") || container;

  const sections = [];
  const cover = ipContainer.querySelector(".ip-cover");
  if (cover) sections.push(cover);
  const topbar = ipContainer.querySelector(".ip-topbar");
  if (topbar) sections.push(topbar);
  ipContainer.querySelectorAll(".ip-day").forEach((s) => sections.push(s));
  const terms = ipContainer.querySelector(".ip-terms");
  if (terms) sections.push(terms);
  const costBreakdown = ipContainer.querySelector(".ip-cost-breakdown");
  if (costBreakdown) sections.push(costBreakdown);
  const footer = ipContainer.querySelector(".ip-footer");
  if (footer) sections.push(footer);

  // Minimum remaining mm before we skip to a new page instead of slicing
  // a tiny sliver. 50mm ≈ one entry card height.
  const MIN_REMAINING = 50;
  let currentY = margin;

  for (const section of sections) {
    const canvas = await html2canvas(section, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pxPerMm = canvas.height / imgHeight;
    const remaining = () => pdfHeight - margin - currentY;

    if (imgHeight <= remaining()) {
      // Fits entirely in remaining space on current page — place as-is.
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.92),
        "JPEG",
        margin,
        currentY,
        imgWidth,
        imgHeight
      );
      currentY += imgHeight + 2;
      continue;
    }

    // Section is taller than remaining space.
    // Slice into the remaining space (if meaningful), then overflow to new pages.
    let srcY = 0;
    while (srcY < canvas.height) {
      const mmAvail = remaining();
      if (mmAvail < MIN_REMAINING) {
        // Too little room to start a useful slice — begin a fresh page.
        pdf.addPage();
        currentY = margin;
      }
      const srcSliceH = Math.min(
        remaining() * pxPerMm,
        canvas.height - srcY
      );
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = Math.ceil(srcSliceH);
      sliceCanvas.getContext("2d").drawImage(canvas, 0, -srcY);
      const renderMm = srcSliceH / pxPerMm;
      pdf.addImage(
        sliceCanvas.toDataURL("image/jpeg", 0.92),
        "JPEG",
        margin,
        currentY,
        imgWidth,
        renderMm
      );
      srcY += srcSliceH;
      currentY += renderMm;
      if (srcY < canvas.height) {
        pdf.addPage();
        currentY = margin;
      }
    }
    currentY += 2;
  }

  container.classList.remove("ip-pdf-export");

  const pdfBlob = pdf.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
  const link = document.createElement("a");
  link.href = pdfUrl;
  link.download = filename;
  link.click();
}

export async function generatePDFFromData(data, filename) {
  const wrapper = document.createElement("div");
  wrapper.style.cssText =
    "position:fixed;left:-9999px;top:0;width:1000px;background:#fff;z-index:-1;overflow:visible;";
  document.body.appendChild(wrapper);

  const root = createRoot(wrapper);

  flushSync(() => {
    root.render(
      <MemoryRouter>
        <ItineraryPreview data={data} />
      </MemoryRouter>
    );
  });

  try {
    await captureAsPDF(wrapper, filename);
  } finally {
    root.unmount();
    document.body.removeChild(wrapper);
  }
}
